import { useCallback, useEffect, useRef, useState } from 'react';

import { pickSupportedMimeType } from '../lib/audioMime';

// PARA-132 (AD-5) — единственный владелец getUserMedia и MediaRecorder в React-
// жизненном цикле. Компоненты и экраны к медиа-API не обращаются.

/** AC-E11: ровно 60 секунд валидны для backend, дольше — 413 reason: 'duration'. */
export const MAX_RECORDING_SECONDS = 60;

/** Шаг таймера записи: elapsedSeconds считается в секундах. */
const TIMER_TICK_MS = 1_000;

export type VoiceRecorderStatus = 'idle' | 'recording';

export interface VoiceRecording {
  blob: Blob;
  /** Нормализованный контейнер, выбранный при старте записи. */
  mimeType: string;
  /** AC-E11: запись оборвал автостоп, а не пользователь. */
  reachedLimit: boolean;
}

interface UseVoiceRecorderOptions {
  /** Вызывается только для завершённой (не отменённой) записи. */
  onComplete: (recording: VoiceRecording) => void;
  /** Ошибка getUserMedia отдаётся наверх как есть — маппит mapVoiceError. */
  onError: (error: unknown) => void;
}

export interface VoiceRecorder {
  status: VoiceRecorderStatus;
  elapsedSeconds: number;
  start: () => void;
  stop: () => void;
  cancel: () => void;
}

export function useVoiceRecorder({ onComplete, onError }: UseVoiceRecorderOptions): VoiceRecorder {
  const [status, setStatus] = useState<VoiceRecorderStatus>('idle');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedRef = useRef(0);
  const isCancelledRef = useRef(false);
  const reachedLimitRef = useRef(false);
  const isStartingRef = useRef(false);
  const isMountedRef = useRef(true);

  // Колбэки читаются в момент срабатывания события: их пересоздание не должно
  // менять идентичность start/stop/cancel.
  const callbacksRef = useRef({ onComplete, onError });
  callbacksRef.current = { onComplete, onError };

  /** AC-3/AC-E12: останавливает таймер и ВСЕ треки — системный индикатор
   * микрофона гаснет. Идемпотентна. */
  const releaseResources = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
  }, []);

  const start = useCallback(() => {
    // AC-2: повторный запуск во время записи (и во время выдачи разрешения) —
    // no-op, второй MediaRecorder не создаётся.
    if (recorderRef.current !== null || isStartingRef.current) {
      return;
    }
    const mimeType = pickSupportedMimeType();
    if (mimeType === null) {
      return;
    }
    isStartingRef.current = true;

    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (!isMountedRef.current) {
          // Экран закрыли, пока пользователь давал разрешение.
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        const recorder = new MediaRecorder(stream, { mimeType });
        streamRef.current = stream;
        recorderRef.current = recorder;
        chunksRef.current = [];
        isCancelledRef.current = false;
        reachedLimitRef.current = false;

        recorder.ondataavailable = (event: BlobEvent) => {
          if (event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };
        recorder.onstop = () => {
          const chunks = chunksRef.current;
          const isCancelled = isCancelledRef.current;
          const reachedLimit = reachedLimitRef.current;
          releaseResources();
          if (isMountedRef.current) {
            setStatus('idle');
            setElapsedSeconds(0);
          }
          if (isCancelled) {
            // AC-3/AC-E12: аудио выброшено, наверх ничего не отдаём.
            return;
          }
          callbacksRef.current.onComplete({
            blob: new Blob(chunks, { type: mimeType }),
            mimeType,
            reachedLimit,
          });
        };

        recorder.start();
        elapsedRef.current = 0;
        setElapsedSeconds(0);
        setStatus('recording');
        timerRef.current = setInterval(() => {
          elapsedRef.current += 1;
          setElapsedSeconds(elapsedRef.current);
          if (elapsedRef.current >= MAX_RECORDING_SECONDS) {
            // AC-E11: записанное не выбрасывается — уходит на транскрипцию.
            reachedLimitRef.current = true;
            recorderRef.current?.stop();
          }
        }, TIMER_TICK_MS);
      } catch (error) {
        releaseResources();
        if (isMountedRef.current) {
          setStatus('idle');
        }
        callbacksRef.current.onError(error);
      } finally {
        isStartingRef.current = false;
      }
    })();
  }, [releaseResources]);

  const stop = useCallback(() => {
    recorderRef.current?.stop();
  }, []);

  const cancel = useCallback(() => {
    isCancelledRef.current = true;
    recorderRef.current?.stop();
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      // AC-E12: уход с экрана во время записи = отмена; ресурсы освобождаются
      // синхронно, состояние размонтированного компонента не обновляется.
      isMountedRef.current = false;
      isCancelledRef.current = true;
      recorderRef.current?.stop();
      releaseResources();
    };
  }, [releaseResources]);

  return { status, elapsedSeconds, start, stop, cancel };
}
